/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { X } from 'react-feather';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import type { SelectChangeEvent } from '@mui/material/Select';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Slider from '@mui/material/Slider';
import type {
  LocationEntity,
  SmartRoutingCustomColumn,
  SmartRoutingEntityType,
  SmartRoutingSearchEntity,
  SmartRoutingV2,
} from '@trinity/chat';
import { AnalysisQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint/index.js';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip';
import { useDebounceValue } from '@trinity/chat-components/hooks/useDebounceValue';
import { BasicSystemUserMessage } from '../Chat/Message.System.Basic';
import { usePlaceholderStyles } from '../Chat/Message.System.styles';
import { getSmartRoutingEntityPluralLabel } from './entity-copy.js';
import { SmartRoutingEntitySelectionTable } from './SmartRoutingEntitySelectionTable';

type Props = {
  onRouteSubmitted?: (selectedEntities: LocationEntity[]) => Promise<void>;
  onExportResults?: () => Promise<void>;
  onValidEntitiesFilterChange?: (entityTypes: SmartRoutingEntityType[]) => void;
  onDistanceRangeFilterChange?: (distanceRange: { minMiles: number; maxMiles: number }) => void;
  onSearchTextChange?: (searchText: string) => void;
  results: SmartRoutingSearchEntity[];
  /** Brand-configured custom column definitions (empty for the legacy HCP index fallback). */
  customColumns?: SmartRoutingCustomColumn[];
  availableEntityTypes: SmartRoutingEntityType[];
  validEntities: SmartRoutingEntityType[];
  distanceRange: { minMiles: number; maxMiles: number };
  /** Committed free-text filter value; the input keeps its own debounced draft. */
  searchText: string;
  /** Any in-flight search; drives the table's loading overlay and nothing else. */
  isLoading: boolean;
  /** First search for this location, before any results exist; replaces the whole body with a skeleton. */
  isInitialLoading: boolean;
  isDisabled: boolean;
  isExportingResults?: boolean;
  startingLocation: SmartRoutingV2['startingLocation'];
};

const SEARCH_TEXT_DEBOUNCE_MS = 350;
const SEARCH_TEXT_MAX_LENGTH = 200;
const DISTANCE_FILTER_MIN_MILES = 0;
const DISTANCE_FILTER_MAX_MILES = 120;
const DISTANCE_FILTER_MID_MILES =
  DISTANCE_FILTER_MIN_MILES + Math.floor((DISTANCE_FILTER_MAX_MILES - DISTANCE_FILTER_MIN_MILES) / 2);
const DISTANCE_FILTER_MARKS = [
  { value: DISTANCE_FILTER_MIN_MILES, label: `${DISTANCE_FILTER_MIN_MILES}` },
  { value: DISTANCE_FILTER_MID_MILES, label: `${DISTANCE_FILTER_MID_MILES}` },
  { value: DISTANCE_FILTER_MAX_MILES, label: `${DISTANCE_FILTER_MAX_MILES}` },
];

function getEntitySelectionKey(entityType: LocationEntity['type'], entityId: string | number): string {
  return `${entityType}:${entityId}`;
}

type CustomerTypeFilterValue = 'both' | SmartRoutingEntityType;

type CurrentTripProps = {
  selectedEntities: SmartRoutingSearchEntity[];
  onRemoveEntity: (entityToRemove: SmartRoutingSearchEntity) => void;
  onClearAllEntities: () => void;
};

function CurrentTrip({
  selectedEntities,
  onRemoveEntity,
  onClearAllEntities,
}: CurrentTripProps): React.JSX.Element {
  const selectedEntitiesByDistance = useMemo(() => {
    return [...selectedEntities].sort((leftEntity, rightEntity) => {
      const leftDistance = leftEntity.distance;
      const rightDistance = rightEntity.distance;

      if (typeof leftDistance !== 'number' && typeof rightDistance !== 'number') {
        return leftEntity.name.localeCompare(rightEntity.name);
      }

      if (typeof leftDistance !== 'number') {
        return 1;
      }

      if (typeof rightDistance !== 'number') {
        return -1;
      }

      return leftDistance - rightDistance;
    });
  }, [selectedEntities]);

  return (
    <CurrentTripContainer>
      <CurrentTripHeader>
        <CurrentTripTitle>Current Trip</CurrentTripTitle>
        <ClearTripButton
          type="button"
          onClick={onClearAllEntities}
          disabled={selectedEntitiesByDistance.length === 0}
        >
          Clear All
        </ClearTripButton>
      </CurrentTripHeader>
      {selectedEntitiesByDistance.length === 0 ? (
        <CurrentTripEmptyState>No stops added</CurrentTripEmptyState>
      ) : (
        <CurrentTripList>
          {selectedEntitiesByDistance.map((entity) => {
            const entityKey = getEntitySelectionKey(entity.entityType, entity.entityId);
            const distanceLabel =
              typeof entity.distance === 'number' ? `${entity.distance.toFixed(1)} mi` : 'Distance N/A';
            const locationLabel = entity.location.address?.trim() || 'Location unavailable';

            return (
              <CurrentTripItem key={entityKey}>
                <CurrentTripItemBody>
                  <CurrentTripEntityName>
                    {entity.name} <CurrentTripEntitySubtitle>({distanceLabel})</CurrentTripEntitySubtitle>
                  </CurrentTripEntityName>
                  <CurrentTripEntityMeta>{locationLabel}</CurrentTripEntityMeta>
                </CurrentTripItemBody>
                <RemoveStopIcon
                  size={16}
                  role="button"
                  tabIndex={0}
                  aria-label={`Remove ${entity.name} from current trip`}
                  onClick={() => {
                    onRemoveEntity(entity);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onRemoveEntity(entity);
                    }
                  }}
                />
              </CurrentTripItem>
            );
          })}
        </CurrentTripList>
      )}
    </CurrentTripContainer>
  );
}

export const SmartRoutingEntitySelection = ({
  onExportResults,
  onRouteSubmitted,
  onValidEntitiesFilterChange,
  onDistanceRangeFilterChange,
  onSearchTextChange,
  ...props
}: Props) => {
  const placeholderStyles = usePlaceholderStyles();
  const searchTextInputId = useId();

  const [selectedEntities, setSelectedEntities] = useState<SmartRoutingSearchEntity[]>([]);
  const [distanceRangeDraft, setDistanceRangeDraft] = useState<[number, number]>([
    props.distanceRange.minMiles,
    props.distanceRange.maxMiles,
  ]);
  const [searchTextDraft, setSearchTextDraft] = useState<string>(props.searchText);

  // The draft keeps typing responsive; only the settled value reaches the server, since
  // every refetch also triggers bulk driving-distance lookups.
  const debouncedSearchText = useDebounceValue(searchTextDraft, SEARCH_TEXT_DEBOUNCE_MS);

  useEffect(() => {
    if (!onSearchTextChange || debouncedSearchText.trim() === props.searchText.trim()) {
      return;
    }

    onSearchTextChange(debouncedSearchText.trim());
  }, [debouncedSearchText, onSearchTextChange, props.searchText]);

  const selectedEntityKeySet = useMemo(() => {
    return new Set(
      selectedEntities.map((entity) => getEntitySelectionKey(entity.entityType, entity.entityId))
    );
  }, [selectedEntities]);

  const toggleEntitySelection = useCallback((result: SmartRoutingSearchEntity) => {
    const selectionKey = getEntitySelectionKey(result.entityType, result.entityId);

    setSelectedEntities((currentSelection) => {
      const alreadySelected = currentSelection.some((entity) => {
        return getEntitySelectionKey(entity.entityType, entity.entityId) === selectionKey;
      });

      if (alreadySelected) {
        return currentSelection.filter((entity) => {
          return getEntitySelectionKey(entity.entityType, entity.entityId) !== selectionKey;
        });
      }

      return [...currentSelection, result];
    });
  }, []);

  const removeEntitySelection = useCallback((entityToRemove: SmartRoutingSearchEntity) => {
    const removeKey = getEntitySelectionKey(entityToRemove.entityType, entityToRemove.entityId);

    setSelectedEntities((currentSelection) => {
      return currentSelection.filter((entity) => {
        return getEntitySelectionKey(entity.entityType, entity.entityId) !== removeKey;
      });
    });
  }, []);

  const clearAllEntitySelections = useCallback(() => {
    setSelectedEntities([]);
  }, []);

  const handleGenerateRoute = useCallback(() => {
    if (selectedEntities.length > 0) {
      if (onRouteSubmitted) {
        const locationEntities: LocationEntity[] = selectedEntities.map((entity) => {
          return {
            id: entity.entityId,
            name: entity.name,
            type: entity.entityType,
          };
        });

        onRouteSubmitted(locationEntities).catch(() => undefined);
      }
    }
  }, [onRouteSubmitted, selectedEntities]);

  const handleExportResults = useCallback(() => {
    if (onExportResults) {
      onExportResults().catch(() => undefined);
    }
  }, [onExportResults]);

  const handleValidEntitiesFilterChange = useCallback(
    (event: SelectChangeEvent<CustomerTypeFilterValue>) => {
      if (!onValidEntitiesFilterChange) {
        return;
      }

      const selection = event.target.value;
      const allowedValues = new Set(props.availableEntityTypes);
      const requestedValues: SmartRoutingEntityType[] =
        selection === 'both' ? ['hcp', 'account'] : [selection];
      const filteredValues = requestedValues.filter((item) => allowedValues.has(item));

      onValidEntitiesFilterChange(filteredValues);
    },
    [props.availableEntityTypes, onValidEntitiesFilterChange]
  );

  const handleSearchTextChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTextDraft(event.target.value);
  }, []);

  const handleClearSearchText = useCallback(() => {
    setSearchTextDraft('');
  }, []);

  const handleDistanceRangeChange = useCallback(
    (_event: Event, value: number | number[]) => {
      if (!Array.isArray(value) || value.length !== 2) {
        return;
      }

      setDistanceRangeDraft([value[0] ?? 0, value[1] ?? 50]);
    },
    [setDistanceRangeDraft]
  );

  const handleDistanceRangeCommitted = useCallback(
    (_event: Event | React.SyntheticEvent, value: number | number[]) => {
      if (!onDistanceRangeFilterChange || !Array.isArray(value) || value.length !== 2) {
        return;
      }

      onDistanceRangeFilterChange({
        minMiles: value[0] ?? 0,
        maxMiles: value[1] ?? 50,
      });
    },
    [onDistanceRangeFilterChange]
  );

  const customerTypeFilterValue = useMemo<CustomerTypeFilterValue>(() => {
    const hasHcp = props.validEntities.includes('hcp');
    const hasAccount = props.validEntities.includes('account');

    if (hasHcp && hasAccount) {
      return 'both';
    }

    if (hasHcp) {
      return 'hcp';
    }

    if (hasAccount) {
      return 'account';
    }

    return 'both';
  }, [props.validEntities]);

  const searchTextPlaceholder = useMemo(() => {
    // Accounts carry no email address, so only advertise it when HCPs are in scope.
    return props.validEntities.includes('hcp')
      ? 'Filter by name, email, or location'
      : 'Filter by name or location';
  }, [props.validEntities]);

  const generateRouteDisabled = useMemo(() => {
    return selectedEntities.length < 2;
  }, [selectedEntities.length]);

  const exportResultsDisabled = useMemo(() => {
    return props.isDisabled || props.isExportingResults === true || !onExportResults;
  }, [props.isDisabled, props.isExportingResults, onExportResults]);

  const showCustomerTypeFilter = useMemo(() => {
    return props.availableEntityTypes.length > 1;
  }, [props.availableEntityTypes.length]);

  const systemCss = useMemo(() => {
    return css({
      maxWidth: '100%',
    });
  }, []);

  const message = useMemo(() => {
    const entityPluralLabel = getSmartRoutingEntityPluralLabel(props.validEntities);

    if (props.results.length > 0) {
      if (props.startingLocation?.type === 'entity' && props.startingLocation?.entityName) {
        return `Here are nearby ${entityPluralLabel} for ${props.startingLocation.entityName}:`;
      }
      return `Here are nearby ${entityPluralLabel} for your selected location:`;
    }

    if (props.searchText) {
      return `No nearby ${entityPluralLabel} match "${props.searchText}".`;
    }

    return `No nearby ${entityPluralLabel} found for your selected location.`;
  }, [props.results.length, props.searchText, props.startingLocation, props.validEntities]);

  // A refetch is surfaced by the table's own loading overlay only, so the surrounding
  // controls stay mounted instead of flickering out on every filter change.
  const showResults = !props.isDisabled && props.results.length > 0;
  const showTable = !props.isDisabled;

  if (props.isInitialLoading) {
    return (
      <BasicSystemUserMessage bodyStyles={systemCss}>
        <div css={placeholderStyles.skeleton}>
          <Skeleton css={placeholderStyles.skeletonItem} variant="text" width="95%" />
          <Skeleton css={placeholderStyles.skeletonItem} variant="text" width="95%" />
          <Skeleton css={placeholderStyles.skeletonItem} variant="text" width="60%" />
        </div>
      </BasicSystemUserMessage>
    );
  }

  return (
    <BasicSystemUserMessage bodyStyles={systemCss}>
      <Message>{message}</Message>
      {showCustomerTypeFilter && (
        <CustomerTypeFilterRow>
          <CustomerTypeFilterLabel>Customer Type:</CustomerTypeFilterLabel>
          <CustomerTypeFilterSelect
            disabled={props.isDisabled}
            value={customerTypeFilterValue}
            onChange={handleValidEntitiesFilterChange}
          >
            <MenuItem value="both">Accounts & HCPs</MenuItem>
            {props.availableEntityTypes.includes('hcp') && <MenuItem value="hcp">HCPs</MenuItem>}
            {props.availableEntityTypes.includes('account') && <MenuItem value="account">Accounts</MenuItem>}
          </CustomerTypeFilterSelect>
        </CustomerTypeFilterRow>
      )}
      <FiltersRow>
        <SearchTextFilterRow>
          <SearchTextFilterLabel htmlFor={searchTextInputId}>Search:</SearchTextFilterLabel>
          <SearchTextFilterInput
            id={searchTextInputId}
            value={searchTextDraft}
            onChange={handleSearchTextChange}
            placeholder={searchTextPlaceholder}
            disabled={props.isDisabled}
            inputProps={{ maxLength: SEARCH_TEXT_MAX_LENGTH }}
            endAdornment={
              searchTextDraft.length > 0 ? (
                <ClearSearchTextIcon
                  size={16}
                  role="button"
                  tabIndex={0}
                  aria-label="Clear search filter"
                  onClick={handleClearSearchText}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleClearSearchText();
                    }
                  }}
                />
              ) : null
            }
          />
        </SearchTextFilterRow>
        <DistanceFilterSection>
          <DistanceFilterHeader>
            <DistanceFilterLabel>Distance (mi):</DistanceFilterLabel>
            <DistanceFilterValue>
              {distanceRangeDraft[0]} - {distanceRangeDraft[1]}
            </DistanceFilterValue>
          </DistanceFilterHeader>
          <DistanceFilterSlider
            value={distanceRangeDraft}
            onChange={handleDistanceRangeChange}
            onChangeCommitted={handleDistanceRangeCommitted}
            valueLabelDisplay="auto"
            marks={DISTANCE_FILTER_MARKS}
            min={DISTANCE_FILTER_MIN_MILES}
            max={DISTANCE_FILTER_MAX_MILES}
            step={1}
            disableSwap={true}
            disabled={props.isDisabled}
          />
        </DistanceFilterSection>
      </FiltersRow>
      {showTable && (
        <SmartRoutingEntitySelectionTable
          results={props.results}
          customColumns={props.customColumns ?? []}
          validEntities={props.validEntities}
          isLoading={props.isLoading}
          selectedEntityKeys={selectedEntityKeySet}
          onToggleEntity={toggleEntitySelection}
        />
      )}
      {showResults && (
        <HintWrapper>
          <AnalysisQueryHint
            disabled={exportResultsDisabled}
            value={props.isExportingResults ? 'Exporting CSV...' : 'Export as CSV'}
            onClick={handleExportResults}
          />
        </HintWrapper>
      )}
      {showResults && <ResultsTripDivider />}
      {showResults && (
        <CurrentTrip
          selectedEntities={selectedEntities}
          onRemoveEntity={removeEntitySelection}
          onClearAllEntities={clearAllEntitySelections}
        />
      )}
      {showResults && (
        <Tooltip
          title={
            generateRouteDisabled ? 'Please select at least 2 Accounts and HCPs to generate a route.' : ''
          }
          placement="top"
        >
          <HintWrapper>
            <AnalysisQueryHint
              disabled={generateRouteDisabled}
              value={'Generate Route'}
              onClick={handleGenerateRoute}
            />
          </HintWrapper>
        </Tooltip>
      )}
    </BasicSystemUserMessage>
  );
};

const Message = styled.div({
  marginBottom: 15,
});

const HintWrapper = styled.div({
  marginTop: 15,
  display: 'inline-flex',
});

const CustomerTypeFilterRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
});

const CustomerTypeFilterLabel = styled.span(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  color: theme.palette.gray.dark1,
  fontSize: 15,
}));

const CustomerTypeFilterSelect = styled(Select<CustomerTypeFilterValue>)({
  minWidth: 180,
  '& .MuiSelect-select': {
    paddingTop: 8,
    paddingBottom: 8,
  },
});

const FiltersRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 24,
  marginBottom: 10,
  // Stacks instead of squeezing the slider once the message body gets narrow.
  flexWrap: 'wrap',
});

const SearchTextFilterRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flex: '1 1 280px',
  minWidth: 0,
  maxWidth: 420,
});

const SearchTextFilterLabel = styled.label(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  color: theme.palette.gray.dark1,
  fontSize: 15,
}));

const SearchTextFilterInput = styled(OutlinedInput)({
  flex: 1,
  minWidth: 180,
  '& .MuiOutlinedInput-input': {
    paddingTop: 8,
    paddingBottom: 8,
  },
});

const ClearSearchTextIcon = styled(X)(({ theme }) => ({
  color: theme.palette.gray.main,
  width: 16,
  height: 16,
  cursor: 'pointer',
  flexShrink: 0,
  outline: 'none',
  '&:focus-visible': {
    boxShadow: `0 0 0 2px ${theme.palette.gray.light2}`,
    borderRadius: 3,
  },
}));

const DistanceFilterSection = styled.div({
  flex: '0 1 300px',
  maxWidth: 300,
  // Keeps the outermost slider mark label from spilling past the row's right edge.
  paddingRight: 10,
});

const DistanceFilterHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  marginBottom: 6,
});

const DistanceFilterLabel = styled.span(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  color: theme.palette.gray.dark1,
  fontSize: 15,
}));

const DistanceFilterValue = styled.span(({ theme }) => ({
  color: theme.palette.gray.main,
  fontSize: 13,
}));

const DistanceFilterSlider = styled(Slider)({
  paddingTop: 6,
  paddingBottom: 6,
  '& .MuiSlider-markLabel': {
    fontSize: 12,
    // Default is 30px, which leaves a visible gap under the track.
    top: 22,
  },
});

const ResultsTripDivider = styled.div(({ theme }) => ({
  height: 1,
  width: '100%',
  backgroundColor: theme.palette.gray.light2,
  margin: '15px 0',
}));

const CurrentTripContainer = styled.div(({ theme }) => ({
  marginBottom: 15,
}));

const CurrentTripHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
});

const CurrentTripTitle = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  marginBottom: 10,
}));

const ClearTripButton = styled.button(({ theme }) => ({
  border: 'none',
  background: 'transparent',
  color: theme.palette.red.main,
  fontFamily: theme.fonts.semiBold,
  fontSize: 14,
  padding: 0,
  cursor: 'pointer',
  marginBottom: 10,
  '&:disabled': {
    color: theme.palette.gray.main,
    cursor: 'not-allowed',
  },
}));

const CurrentTripEmptyState = styled.div(({ theme }) => ({
  color: theme.palette.gray.main,
}));

const CurrentTripList = styled.ul({
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
});

const CurrentTripItem = styled.li(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  alignItems: 'flex-start',
  border: `1px solid ${theme.palette.gray.light2}`,
  borderRadius: 6,
  padding: 10,
}));

const CurrentTripItemBody = styled.div({
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
});

const CurrentTripEntityName = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 15,
}));

const CurrentTripEntityMeta = styled.div(({ theme }) => ({
  color: theme.palette.gray.main,
  fontSize: 14,
  maxWidth: 420,
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
}));

const CurrentTripEntitySubtitle = styled.span(({ theme }) => ({
  color: theme.palette.gray.main,
  fontSize: 14,
}));

const RemoveStopIcon = styled(X)(({ theme }) => ({
  color: theme.palette.red.main,
  width: 16,
  height: 16,
  cursor: 'pointer',
  flexShrink: 0,
  outline: 'none',
  '&:focus-visible': {
    boxShadow: `0 0 0 2px ${theme.palette.red.secondary}`,
    borderRadius: 3,
  },
}));
